/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import { Promise } from 'rsvp';
import { restartableTask, timeout } from 'ember-concurrency';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { defaultErrorHandler, type TError } from 'volta/utils/error-utils';

import type { IntlService } from 'ember-intl';
import type Infinity from 'ember-infinity/services/infinity';
import type StoreService from 'volta/services/store';
import type { BvSelectCreateSignature, DataOption, SelectedOption } from '../bv-select-create';
import type { PowerSelectArgs, Select } from 'ember-power-select/components/power-select';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';

type BvSelectCreateArgs<O = DataOption> = Pick<
  BvSelectCreateSignature<O>['Args'],
  | 'allowClear'
  | 'disabled'
  | 'dropdownClass'
  | 'dropdownSize'
  | 'error'
  | 'fill'
  | 'helpText'
  | 'horizontalPosition'
  | 'initiallyOpened'
  | 'infiniteScroll'
  | 'inputClasses'
  | 'isEdited'
  | 'isRequired'
  | 'label'
  | 'labelClass'
  | 'labelHidden'
  | 'labelInfo'
  | 'labelWrapperClass'
  | 'matchTriggerWidth'
  | 'multiple'
  | 'noMatchesMessage'
  | 'onBlur'
  | 'onCreate'
  | 'onFocus'
  | 'onKeydown'
  | 'options'
  | 'placeholder'
  | 'preventScroll'
  | 'renderAll'
  | 'searchField'
  | 'triggerClass'
  | 'triggerAutoFocus'
>;

export interface DomainSelectComponentSignature<O = DataOption, SubArgs = unknown> {
  Args: BvSelectCreateArgs<O> & {
    /** The model id key */
    idKey?: string;
    /** Whether we should auto select the single item in the list if no selected value is specified */
    selectSingleValueIfEmpty?: boolean;
    value?: string | null;
    propertyKey?: string;
    values?: string[];
    excludedOptions?: O[];
    internalError?: boolean;
    deferOptionsFetch?: boolean;
    inline?: BvSelectCreateSignature['Args']['labelInline'];
    onChange?: (ids: string[], values: SelectedOption<O>[]) => void;
    searchPromise?: (payload: Record<string, any>) => Promise<any[]>;
    beforeQuery?: (query: IJsonApiQuery) => IJsonApiQuery;
    registerAPI?: PowerSelectArgs['registerAPI'];
  } & SubArgs;
  Blocks: {
    default: [any, Select];
  };
  Element: HTMLElement;
}

export default class DomainSelectComponent<O = DataOption, SubArgs = unknown> extends Component<
  DomainSelectComponentSignature<O, SubArgs>
> {
  // Services
  // ~~~~~
  @service infinity!: Infinity;
  @service store!: StoreService;
  @service intl!: IntlService;

  // Properties
  // ~~~~~

  @tracked
  options = this.args.options ?? [];

  @tracked
  internalError = this.args.internalError ?? false;

  get idKey() {
    return this.args.idKey;
  }

  get values() {
    return this.args.values ?? [];
  }

  get value() {
    return this.args.value;
  }

  get excludedOptions() {
    return this.args.excludedOptions ?? [];
  }

  get multiple() {
    return this.args.multiple ?? false;
  }

  get propertyKey() {
    return this.args.propertyKey;
  }

  get infiniteScroll() {
    return this.args.infiniteScroll ?? false;
  }

  get placeholder() {
    return this.args.placeholder;
  }

  get renderAll() {
    return this.args.renderAll;
  }

  get deferOptionsFetch() {
    return this.args.deferOptionsFetch ?? false;
  }

  // Computed Properties
  // ~~~~~

  get selectedOptions() {
    const { values, idKey, options } = this;
    if (!idKey) {
      return values;
    } else {
      // @ts-ignore
      return this._onSearchSelectedOptionsSuccess(options, idKey, values);
    }
  }

  get selectedOption() {
    const { value, idKey, options } = this;

    if (!idKey) {
      return value;
    } else {
      // @ts-ignore
      return this._onSearchSelectedOptionSuccess(options, idKey, value);
    }
  }

  // Lifecycle Hooks
  // ~~~~~

  // Actions
  // ~~~~~

  @action
  handleOnChange(
    selected: SelectedOption<O> | SelectedOption<O>[],
    _select: Select,
    _e: Event | undefined
  ) {
    if (this.multiple) {
      return this.valuesDidChange(selected as SelectedOption<O>[]);
    } else {
      return this.valueDidChange(selected as SelectedOption<O>);
    }
  }

  @action
  valuesDidChange(options: SelectedOption<O>[]) {
    const { idKey } = this;
    return this.args.onChange?.(
      options.map((o) => getValueFromOption(o, idKey)),
      options
    );
  }

  @action
  valueDidChange(value: SelectedOption<O>) {
    const { idKey } = this;
    const idValue = value && idKey ? ((value as any)[idKey] as string) : (value as string);
    return this.args.onChange?.([idValue], [value]);
  }

  /**
   * Checks whether the BvSelectCreate component should show the create button
   *
   * @param {string} term Searched term
   * @param {Object[]} options Select options
   * @returns {boolean}
   */
  @action
  showCreateWhen(term?: string, options?: O[]) {
    const { propertyKey } = this.args;

    return (
      isPresent(term) &&
      !options?.some((o) => (propertyKey ? o[propertyKey as keyof O] === term : o === term))
    );
  }

  /**
   * Performs a search task when search term is empty (avoid empty results)
   *
   * @param {string} term Searched term
   */
  @action
  onInput(term?: string) {
    if (!term || !term.length) {
      this.searchTask.perform(term);
    }
  }

  // Helper Functions
  // ~~~~~

  searchTask = restartableTask(async (term?: string) => {
    try {
      this.internalError = false;
      await timeout(isPresent(term) ? 600 : 0);

      const options = await this.searchPromise(this.payloadFromTerm(term));
      this.options = options;
      if (
        !term &&
        options.length === 1 &&
        this.args.selectSingleValueIfEmpty &&
        !this.values.length &&
        !this.args.value
      ) {
        const option = options.slice()[0];
        if (this.args.multiple) {
          this.valuesDidChange(options);
        } else {
          this.valueDidChange(option);
        }
      }
      return options;
    } catch (error) {
      defaultErrorHandler(error as TError);
      this.internalError = true;
      return [];
    }
  });

  async searchPromise(payload: Record<string, any>) {
    return this.args.searchPromise?.(payload) ?? [];
  }

  payloadFromTerm(term?: string) {
    const { values = [], value, excludedOptions = [], idKey } = this;

    return {
      search: term,
      selected: values?.length ? values : value ? [value] : [],
      excluded: excludedOptions.reduce((acc: string[], o) => {
        const v = getValueFromOption(o, idKey);
        if (v != null) {
          acc.push(v);
        }
        return acc;
      }, [])
    };
  }

  // Private Callbacks/Handlers
  // ~~~~~

  _onSearchSelectedOptionsSuccess(options: any[], idKey: string, values: any[]) {
    if (this.isDestroyed || this.isDestroying) return [];

    let newOptions = options.filter((i) => !i.__isSuggestion__);

    if (this.args.onCreate && !idKey) {
      newOptions = [...new Set([...(values ?? []).concat(newOptions)])];
    }
    return newOptions.filter((item: O) => {
      return values.includes(getValueFromOption(item, idKey));
    });
  }

  _onSearchSelectedOptionSuccess(options: any[], idKey: string, value: any) {
    if (this.isDestroyed || this.isDestroying) return;

    let newOptions = options.filter((i) => !i.__isSuggestion__);

    if (this.args.onCreate && !idKey) {
      newOptions = [...new Set([...(value ? [value] : []).concat(newOptions)])];
    }

    return idKey
      ? newOptions && newOptions.length && value != null
        ? newOptions.find((o) => getValueFromOption(o, idKey) === value)
        : null
      : value;
  }
}

const getValueFromOption = <O = DataOption>(o: O, idKey: string = 'id') => {
  return typeof o === 'string' ? o : (o[idKey as keyof O] as string);
};

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    DomainSelect: typeof DomainSelectComponent;
    'domain-select': typeof DomainSelectComponent;
  }
}
